/**
 * 16c.1 (security): the browser's double-submit CSRF token (ADR-0003, D-291). The bireysel site lives under the kurumsal
 * one's domain (consumer.oppy.… under oppy.…), so its pages can read kurumsal's `__Secure-dbrain_csrf` too, and the
 * other way round a host-only `__Host-dbrain_csrf` may sit beside a shared one. A page therefore reads only the cookie
 * its own API names (`GET /api/auth/csrf` says which: `__Host-`, `__Secure-` or plain on http), never the other
 * edition's, and asks for a fresh token whenever its own is missing.
 */
export interface CsrfSource {
  /** `document.cookie` */
  cookies: () => string;
  /** `GET /api/auth/csrf`: a token and the cookie's name (an API before 16c.1 names none) */
  fetch: () => Promise<{ token: string; cookie?: string }>;
}

export function readCookie(cookies: string, name: string): string | undefined {
  for (const part of cookies.split(';')) {
    const [key, ...rest] = part.trim().split('=');
    if (key === name) return decodeURIComponent(rest.join('='));
  }
  return undefined;
}

export function createCsrf(source: CsrfSource): { token: () => Promise<string> } {
  let name: string | undefined;
  const fresh = async (): Promise<string> => {
    const answer = await source.fetch();
    if (answer.cookie) name = answer.cookie;
    return answer.token;
  };
  return {
    async token() {
      // the first write of the page learns the name (and gets a token with it)
      if (!name) return fresh();
      return readCookie(source.cookies(), name) ?? fresh();
    },
  };
}
